'use client';
import { useEffect } from 'react';
/**
 * React Scan component for development
 *
 * Automatically enables react-scan in development mode only.
 * Can be explicitly enabled/disabled via the `enabled` prop.
 * Uses dynamic import to avoid bundling in production.
 *
 * @example
 * ```tsx
 * // app/layout.tsx
 * import { ReactScan } from '@sandstone/dev-tools';
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         {children}
 *         <ReactScan />
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 */
export function ReactScan({ enabled }) {
    const shouldLoad = enabled ?? process.env.NODE_ENV === 'development';
    useEffect(() => {
        if (shouldLoad) {
            // Dynamic import to avoid bundling react-scan in production
            import('react-scan')
                .then(({ scan }) => {
                scan({
                    enabled: true,
                    log: false,
                });
            })
                .catch((e) => {
                console.warn('[ReactScan] Failed to load:', e);
            });
        }
        else {
            // When disabled, try to clean up react-scan
            // Remove any react-scan UI elements
            const reactScanElements = document.querySelectorAll('[data-react-scan], [data-react-scan-root]');
            for (const el of reactScanElements) {
                el.remove();
            }
            // Try to disable via window API if available
            const win = window;
            if (typeof window !== 'undefined' && win.reactScan) {
                try {
                    win.reactScan.cleanup?.();
                }
                catch (e) {
                    console.warn('[ReactScan] Error during cleanup:', e);
                }
            }
        }
        return () => {
            // Additional cleanup when component unmounts
            const reactScanElements = document.querySelectorAll('[data-react-scan], [data-react-scan-root]');
            for (const el of reactScanElements) {
                el.remove();
            }
        };
    }, [shouldLoad]);
    return null;
}
